import * as React from "react";
import { Box, Typography, Button, Divider, Grid, Chip } from "@mui/material";
import { Image } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import NewCard from "../../app/components/NewCard";
import { main_button } from "../../app/components/Styles";
import { connect } from "react-redux";
import { getReturnRequestById, approveReturnRequest, rejectReturnRequest } from "../../app/store/actions/dataActions";
import { useEffect } from "react";
import moment from "moment";
import { ReasonModal, RefundModal } from "../../app/components";
import { useTranslation } from "react-i18next";

const ReturnRequest = ({ getReturnRequestById, approveReturnRequest, rejectReturnRequest, returnRequest, isLoading }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    getReturnRequestById({ returnRequestId: id });
  }, [id]);

  const [refundOpen, setRefundOpen] = React.useState(false);
  const handleApprove = () => setRefundOpen(true);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const handleConfirmRefund = async ({ refundAmount }) => {
    try {
      setIsSubmitting(true);
      await approveReturnRequest({ returnRequestId: id, refundAmount: parseFloat(refundAmount || 0) });
      setRefundOpen(false);
      await getReturnRequestById({ returnRequestId: id });
    } finally {
      setIsSubmitting(false);
    }
  };

  const [reasonOpen, setReasonOpen] = React.useState(false);
  const handleReject = () => setReasonOpen(true);
  const handleConfirmReject = async (reason) => {
    try {
      setIsSubmitting(true);
      await rejectReturnRequest({ returnRequestId: id, reason });
      setReasonOpen(false);
      await getReturnRequestById({ returnRequestId: id });
    } finally {
      setIsSubmitting(false);
    }
  };

  const data = returnRequest;
  const statusLabels = {
    Pending: t("returnsSystem.status.pending"),
    Accepted: t("returnsSystem.status.accepted"),
    Rejected: t("returnsSystem.status.rejected"),
  };

  const getItemUnitPrice = (it) => {
    if (it?.orderItem?.price != null) {
      return parseFloat(it.orderItem.price) - (parseFloat(it.orderItem.discount) || 0);
    }
    return parseFloat(it?.product?.price) || 0;
  };

  const calculatedRefundAmount = (data?.incomingItems || []).reduce((sum, it) => {
    return sum + (getItemUnitPrice(it) * (parseInt(it?.quantity, 10) || 0));
  }, 0);

  return (
    <Box sx={{ width: "100%", paddingX: 5, py: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography fontWeight={"bold"} fontSize={20}>{t("returnsSystem.returnRequest.title")}</Typography>
        <Button sx={{ ...main_button }} onClick={() => navigate(-1)}>{t("common.back")}</Button>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <NewCard content={<>
            <Typography>{t("returnsSystem.returnRequest.request")} {id}</Typography>
            {data?.originalOrder?.shopifyOrderNumber && <Typography>{t("returnsSystem.returnRequest.shopify")}: {data?.originalOrder?.shopifyOrderNumber}</Typography>}
            <Typography>{t("returnsSystem.returnRequest.createdAt")} {data?.createdAt ? moment(data?.createdAt).format("lll") : '-'}</Typography>
            <Typography fontWeight="bold">{t("common.status")}: {statusLabels[data?.status] || data?.status}</Typography>
            <Typography fontWeight="bold">{t("common.reason")}: {data?.reason}</Typography>
            {data?.rejectedAt && <Typography>{t("returnsSystem.returnRequest.rejectedAt")} {moment(data?.rejectedAt).format("lll")}</Typography>}
            {data?.rejectedReason && <Typography>{t("returnsSystem.returnRequest.rejectedReason")}: {data?.rejectedReason}</Typography>}
          </>} />
        </Grid>
        <Grid item xs={12} md={8}>
          <NewCard content={<>
            <Typography variant="h6" textAlign="center">{t("returnsSystem.returnRequest.originalOrderTitle")}</Typography>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Typography>{t("returnsSystem.returnRequest.order")}: {data?.originalOrder?._id}</Typography>
              <Typography>{t("common.status")}: {data?.originalOrder?.status}</Typography>
              <Typography>{t("returnsSystem.returnRequest.deliveredAt")}: {data?.originalOrder?.deliveredAt ? moment(data?.originalOrder?.deliveredAt).format('lll') : '-'}</Typography>
            </Box>
          </>} />

          <NewCard sx={{ mt: 2 }} content={<>
            <Typography variant="h6" textAlign="center">{t("returnsSystem.returnRequest.incomingItemsTitle")}</Typography>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={5} md={5}>
                  <Typography fontWeight={"bold"}>{t("returnsSystem.returnRequest.productName")}</Typography>
                </Grid>
                <Grid item xs={3} md={3}>
                  <Typography fontWeight={"bold"}>{t("common.sku")}</Typography>
                </Grid>
                <Grid item xs={2} md={2}>
                  <Typography fontWeight={"bold"}>{t("common.quantity")}</Typography>
                </Grid>
                <Grid item xs={2} md={2}>
                  <Typography fontWeight={"bold"}>{t("common.price")}</Typography>
                </Grid>
                {data?.incomingItems?.map((it, idx) => (
                  <React.Fragment key={idx}>
                    <Grid item xs={5} md={5}>
                      <Typography>{it?.product?.name}</Typography>
                    </Grid>
                    <Grid item xs={3} md={3}>
                      <Typography>{it?.product?.sku || it?.product?.externalId || '-'}</Typography>
                    </Grid>
                    <Grid item xs={2} md={2}>
                      <Typography>{it?.quantity}</Typography>
                    </Grid>
                    <Grid item xs={2} md={2}>
                      <Typography>{it?.orderItem?.price ? (it?.orderItem?.price - (it?.orderItem?.discount || 0)) : it?.product?.price} EGP</Typography>
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </Box>
          </>} />

          <NewCard sx={{ mt: 2 }} content={<>
            <Typography variant="h6" textAlign="center">{t("returnsSystem.returnRequest.customerAdditionalInfoTitle")}</Typography>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Typography>{data?.additionalInfo || t("returnsSystem.returnRequest.noAdditionalInfo")}</Typography>
            </Box>
          </>} />

          <NewCard sx={{ mt: 2 }} content={<>
            <Typography variant="h6" textAlign="center">{t("returnsSystem.returnRequest.attachmentsTitle")}</Typography>
            <Divider />
            <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {data?.attachments && data.attachments.length > 0 ? (
                <Image.PreviewGroup>
                  {data.attachments.map((url, idx) => (
                    <Image
                      key={idx}
                      src={url}
                      alt={`attachment-${idx}`}
                      style={{ borderRadius: 4 }}
                      height={120}
                      width={160}
                    />
                  ))}
                </Image.PreviewGroup>
              ) : (
                <Typography>{t("returnsSystem.returnRequest.noAttachments")}</Typography>
              )}
            </Box>
          </>} />

          {data?.status === 'Pending' && (
            <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button sx={{ ...main_button }} onClick={handleApprove} disabled={isSubmitting}>{t("returnsSystem.returnRequest.approve")}</Button>
              <Button sx={{ ...main_button }} onClick={handleReject} disabled={isSubmitting}>{t("returnsSystem.returnRequest.reject")}</Button>
            </Box>
          )}
        </Grid>
      </Grid>
      <ReasonModal
        open={reasonOpen}
        onClose={() => !isSubmitting && setReasonOpen(false)}
        onConfirm={handleConfirmReject}
        title={t("returnsSystem.returnRequest.rejectTitle")}
        message={t("returnsSystem.returnRequest.rejectMessage")}
      />
      <RefundModal
        open={refundOpen}
        onClose={() => !isSubmitting && setRefundOpen(false)}
        onConfirm={handleConfirmRefund}
        title={t("returnsSystem.returnRequest.approveTitle")}
        message={t("returnsSystem.returnRequest.approveMessage")}
        showType={false}
        isLoading={isSubmitting}
        initialRefundAmount={calculatedRefundAmount}
      />
    </Box>
  );
};

const mapStateToProps = (state) => ({
  returnRequest: state?.records?.returnRequest,
  isLoading: state?.records?.isLoading,
});

const mapDispatchToProps = { getReturnRequestById, approveReturnRequest, rejectReturnRequest };

export default connect(mapStateToProps, mapDispatchToProps)(ReturnRequest);

